/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useRef, useEffect, useContext } from 'react';
import { Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step, StoreHelpers } from 'react-joyride';

import { ThemeContext } from '../../contexts/ThemeContext';

import DemoService from '../../services/DemoService';

import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';
import GeneralNavbar from '../general/GeneralNavbar';
import './demo.css';

const SignIn = props => {

    // contexts
    const { theme, colors, fontFamily, fontSize, fontWeight } = useContext(ThemeContext);

    // states
    const [user, setUser] = useState({ email: "", password: "", isTwoFactor: false, otp: "", rememberMe: false });
    const [message, setMessage] = useState(null);
    const [modalMessage, setModalMessage] = useState(null);
    const [showResend, setShowResend] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resetPasswordModal, setResetPasswordModal] = useState({ isVisible: false, email: "" });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>Let's begin our journey!</h2>,
                placement: 'center',
                target: '#loginDiv',
            },
            {
                content: <h2>This is our sign in form</h2>,
                floaterProps: {
                    disableAnimation: true,
                },
                spotlightPadding: 30,
                placement: 'right',
                target: '#loginForm',
            },
            {
                content: 'Enter your email here',
                placement: 'bottom',
                styles: {
                    options: {
                        width: 300,
                    },
                },
                target: '#emailInput',
            },
            {
                content: (
                    <div>
                        Enter your password here
                        <br />
                        <h3>It's safe!</h3>
                        <br />Also look for <span><i className="fas fa-lock"></i></span> this icon in your address bar to make sure that your connection is secure.
                    </div>
                ),
                placement: 'top',
                target: '#passwordInput',
            },
            {
                content: (
                    <div>
                        Click here to sign in
                    </div>
                ),
                placement: 'top',
                target: '#submitButton',
            },
            {
                content: (
                    <div>
                        Enter your otp here
                        <br />
                        <h3>You can found this in your email</h3>
                    </div>
                ),
                placement: 'top',
                target: '#otpInput',
            },
            {
                content: (
                    <div>
                        In case you want to be logged in for long time.
                    </div>
                ),
                placement: 'top',
                target: '#rememberInput',
            },
            {
                content: (
                    <div>
                        Forgot your password?
                        <br />
                        <h3>We got your back!</h3>
                    </div>
                ),
                placement: 'top',
                target: '#forgotInput',
            },
            {
                content: (
                    <div>
                        Don't have an account?
                        <br />
                        <h3>Click here to sign up</h3>
                    </div>
                ),
                placement: 'top',
                target: '#registerInput',
            }
        ]
    });
    let timerID = useRef(null);
    let helpers = null;

    useEffect(() => {
        document.getElementById("emailInput").focus();
        DemoService.getUserData().then(data => {
            // console.log("data2", data);
            if (data.isAuthenticated) {
                if (data.user.roles[0] === "DemoClient") {
                    props.history.replace('/demo/client/dashboard')
                } else if (data.user.roles[0] === "DemoProvider") {
                    props.history.replace('/demo/counsellor/sessions')
                }
            }
        })
        return () => {
            clearTimeout(timerID)
        }
    }, [])

    useEffect(() => {
        if (showOtp) {
            document.getElementById("otpInput").focus();
        }
    }, [showOtp])

    // handle functions
    const handleChange = e => {
        setUser({ ...user, [e.target.name]: e.target.value });
    }

    const handleSubmit = e => {
        e.preventDefault();
        if (user.isTwoFactor) {
            if (user.otp.length === 6) {
                DemoService.signin(user)
                    .then(data => {
                        // console.log("data", data);
                        const { isAuthenticated, user, message } = data;
                        if (isAuthenticated) {
                            setMessage(message);
                            if (user.roles[0] === "DemoClient") {
                                // console.log("Client", user);
                                props.history.push('/demo/client/dashboard');
                            } else if (user.roles[0] === "DemoProvider") {
                                // console.log("Provider", user);
                                props.history.push('/demo/counsellor/sessions');
                            }
                        }
                    })
                    .catch(error => {
                        setMessage(error.message);
                    });
            } else {
                setMessage({ msgError: true, msgBody: "Otp should be 6 digits long" });
            }
        } else {
            setShowOtp(true);
            setUser({ ...user, isTwoFactor: true });
        }
    }

    const handlePasswordReset = () => {
        if (resetPasswordModal.email) {
            setModalMessage(null);
            setIsLoading(true);
            DemoService.resetPassword(resetPasswordModal.email).then(data => {
                setMessage(data.message);
                setIsLoading(false);
                setResetPasswordModal({ isVisible: false, email: "" });
            }).catch(error => {
                setModalMessage(error.message);
                setIsLoading(false);
            });
        } else {
            setModalMessage({ msgError: true, msgBody: "Enter your registered email" });
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        <div>
            <Joyride
                callback={handleJoyrideCallback}
                continuous={true}
                getHelpers={getHelpers}
                run={tutorial.status}
                scrollToFirstStep={true}
                showProgress={true}
                showSkipButton={true}
                steps={tutorial.steps}
                styles={{
                    options: {
                        primaryColor: "rgb(56, 78, 115)",
                        zIndex: 10000,
                    },
                }}
            />
            <GeneralNavbar history={props.history} isDemoPage={true} />
            <div id="loginDiv" className="p-3 col-md-4 mt-5 mb-5 offset-md-4" style={theme.form}>
                <form id="loginForm" onSubmit={handleSubmit} style={{ paddingLeft: "10%", paddingRight: "10%" }}>
                    <h2 style={theme.heading}>Sign In to your demo account</h2>
                    <br />

                    {message ? <Message message={message} /> : null}

                    <label htmlFor="email" style={theme.formBodyText} >Email </label>
                    <input id="emailInput"
                        type="text"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        className="form-control"
                        required
                        style={theme.formInput}
                    />
                    <br />

                    <label htmlFor="password" style={theme.formBodyText} >Password </label>
                    <input id="passwordInput"
                        type="password"
                        name="password"
                        value={user.password}
                        onChange={handleChange}
                        className="form-control"
                        required
                        style={theme.formInput}
                    />

                    {
                        showOtp ?
                            <div>
                                <label htmlFor="otp" style={theme.formBodyText} >OTP </label>
                                <input type="text"
                                    id="otpInput"
                                    name="otp"
                                    value={user.otp}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                    placeholder="Type any 6 digit number as OTP"
                                    style={theme.formInput}
                                />
                                {/* <label style={{ fontSize: fontSize.sm }}>*Write any number as otp</label> */}
                            </div>
                            : null
                    }

                    <div className="d-flex mt-2">
                        <div className="form-check" id="rememberInput">
                            <input className="form-check-input" type="checkbox" style={{ width: "1rem", height: "1rem" }} checked={user.rememberMe} id="rm" onChange={(e) => setUser({ ...user, rememberMe: e.target.checked })} />
                            <label className="form-check-label" htmlFor="rm">Remember me</label>
                        </div>
                        <div className="ml-auto">
                            <label style={{ fontFamily: fontFamily.primary, fontSize: "0.9rem", color: colors.primaryGreenDark, cursor: "pointer" }} id="forgotInput" onClick={() => setResetPasswordModal({ isVisible: true, email: user.email })}>
                                Forgot password?
                            </label>
                        </div>
                    </div>

                    <button id="submitButton"
                        className="btn mt-2"
                        type="submit"
                        style={theme.formSubmit}>Sign In</button>

                    <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm, marginTop: "10px" }}>
                        Don't have an demo account?
                        <Link to="/demo/signupRequest" id="registerInput">
                            <span style={{ color: colors.primaryGreenDark, fontWeight: fontWeight.bold }}> Request here</span>
                        </Link>
                    </p>
                </form>
            </div>
            <ErrorBoundary errorAt="At Demo SignIn Modal">
                <Modal size="sm" isOpen={resetPasswordModal.isVisible} backdrop={true}>
                    <ModalHeader id="modal-header" close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => { setResetPasswordModal({ isVisible: false, email: "" }); setModalMessage(null); }}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Reset password</span></ModalHeader>
                    <ModalBody>
                        {modalMessage ? <Message message={modalMessage} /> : null}
                        <Form>
                            <FormGroup>
                                <Label id="modal-email-label">Email</Label>
                                <Input id="modal-email-input" type="text" value={resetPasswordModal.email} onChange={(e) => setResetPasswordModal({ ...resetPasswordModal, email: e.target.value })} />
                            </FormGroup>
                        </Form>
                        <p id="instructions" style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>By clicking on Reset button below a new password will get generated and will be sent to your registered email.</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button id="updateBtn" type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handlePasswordReset()} disabled={isLoading}>
                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Reset
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </div>
    )


}

export default SignIn;